import { reactive, watchEffect } from "vue";
import useLeningdelen from "@/store/financiering/tabs/useLeningdelen";
import useKlantgegevens from "@/store/financiering/tabs/useKlantgegevens";
import { asCur } from "@/helpers/inputHelpers";
import type {
	ToetsHuishoudenParameters,
	ToetsHuishoudenWoonqoute,
} from "@/models/ToetsHuishouden";
import { getWoonquote, getWoonquoteCutoffs } from "@/helpers/woonquoteHelpers";
import schema from "@/store/financiering/schemas/optimalisatieSchema";
import { useParameters } from "../useParameters";
import type { OptimaleHypotheek } from "@/models/domain/OptimaleHypotheek";
import { calculateTotalYearlyIncome } from "@/helpers/inventarisation/incomeCalculations";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { getMonthSpan } from "@loanlink-nl/shared";
import { storeToRefs } from "pinia";
import { useCalculationDate } from "@/composables/useCalculationDate";
import { isEligibleForAOW } from "@/composables/useAOWEligibility";

interface OptimalisatieValues {
	toetsinkomen?: number;
	toetsrenteDeel1?: number;
	toetsrenteDeel2?: number;
	looptijdDeel1?: number;
	looptijdDeel2?: number;
	optimaleHoogteDeel1?: number;
	optimaleHoogteDeel2?: number;
	maximaleHypotheek?: number;
	error?: string;
	warning?: string;
}

export default function useOptimalisatie() {
	const state = reactive({
		schema: schema,
		values: {} as OptimalisatieValues,
	});

	const leningdelenStore = useLeningdelen();
	const {
		computedValues,
		maxLeningExtra,
		mortgagePartsTotal,
		computedLeningdelen,
	} = storeToRefs(leningdelenStore);

	const klantgegevens = useKlantgegevens();
	const { calculationDate, calculationDateString, passingDate } =
		useCalculationDate();
	const dossierDataStore = useDossierDataStore();
	const { data } = storeToRefs(dossierDataStore);

	watchEffect(() => {
		state.values.toetsinkomen = computedValues.value.toetsinkomenTbvWQ;
	});

	watchEffect(() => {
		let toetsrente1 = asCur(computedLeningdelen.value[0]?.toetsrente);
		if (!Number.isFinite(toetsrente1)) {
			toetsrente1 = undefined;
		}
		state.values.toetsrenteDeel1 = toetsrente1;
	});

	watchEffect(() => {
		let toetsrente2 = asCur(computedLeningdelen.value[1]?.toetsrente);
		if (!Number.isFinite(toetsrente2)) {
			toetsrente2 = undefined;
		}
		state.values.toetsrenteDeel2 = toetsrente2;
	});

	watchEffect(() => {
		const part = data.value.mortgage.mortgageParts[0];

		state.values.looptijdDeel1 =
			part?.durationMonths ??
			(part?.endDate
				? getMonthSpan(passingDate.value, part.endDate)
				: undefined);
	});

	watchEffect(() => {
		const part = data.value.mortgage.mortgageParts[1];

		state.values.looptijdDeel2 =
			part?.durationMonths ??
			(part?.endDate
				? getMonthSpan(passingDate.value, part.endDate)
				: undefined);
	});

	function getOptimaleHypotheekHoogte(
		huishouden: ToetsHuishoudenParameters,
		goalRente: number,
		jaarVanAfsluiten: number,
		looptijd: number,
	) {
		const huishoudenMetWoonquote: ToetsHuishoudenWoonqoute = {
			toetsinkomen: asCur(computedValues.value.toetsinkomen) ?? 0,
			bkrVermelding: huishouden.bkrVermelding,
			erfpachtlastPerMaand: huishouden.erfpachtlastPerMaand,
			woonquote: getWoonquote(huishouden, goalRente, jaarVanAfsluiten) ?? 0,
		};

		const hypotheek: OptimaleHypotheek = {
			rente: goalRente,
			woonquote: huishoudenMetWoonquote.woonquote,
			maandlast: leningdelenStore.getToegestaneMaandlast(
				huishoudenMetWoonquote,
			),
		};

		const annuiteitenFactor = leningdelenStore.getAnnuiteitenfactor(
			goalRente,
			looptijd,
		);

		hypotheek.hoogte =
			(hypotheek.maandlast ?? NaN) / annuiteitenFactor + maxLeningExtra.value;

		return hypotheek;
	}

	function resetState() {
		state.values.optimaleHoogteDeel1 = undefined;
		state.values.optimaleHoogteDeel2 = undefined;
		state.values.maximaleHypotheek = undefined;
		state.values.error = undefined;
		state.values.warning = undefined;
	}

	watchEffect(() => {
		resetState();

		if (data.value.mortgage.mortgageParts?.length > 2) {
			state.values.error =
				"De optimalisatie van de leningdeelhoogtes werkt op dit moment alleen met twee leningdelen.";
			return;
		}

		const deel1 = data.value.mortgage.mortgageParts[0];
		const deel2 = data.value.mortgage.mortgageParts[1];

		if (
			deel1 === undefined ||
			deel2 === undefined ||
			deel1.interestRate === deel2.interestRate
		) {
			const rawMax = asCur(computedValues.value.maxLeningInkomenLasten) ?? 0;
			const maxLening = Math.max(rawMax - 1, 0);
			if (deel2 === undefined || deel1?.interestRate === deel2.interestRate) {
				state.values.optimaleHoogteDeel1 = maxLening;
				state.values.optimaleHoogteDeel2 = undefined;
			} else if (deel1 === undefined) {
				state.values.optimaleHoogteDeel2 = maxLening;
				state.values.optimaleHoogteDeel1 = undefined;
			}

			return;
		}

		if (
			deel1.interestDeductionLaw === "none" ||
			deel2.interestDeductionLaw === "none"
		) {
			state.values.error =
				"Op dit moment werkt de optimalisatie van 2 leningdeelhoogtes alleen met leningdelen in box 1.";
			return;
		}

		if (state.values.looptijdDeel1 !== state.values.looptijdDeel2) {
			state.values.error =
				"Op dit moment kunnen alleen de hoogtes van leningdelen met dezelfde looptijd geoptimaliseerd worden.";
			return;
		}

		let toetsrente1: number | undefined;
		if (typeof state.values.toetsrenteDeel1 === "number") {
			toetsrente1 = state.values.toetsrenteDeel1;
		}

		let toetsrente2: number | undefined;
		if (typeof state.values.toetsrenteDeel2 === "number") {
			toetsrente2 = state.values.toetsrenteDeel2;
		}

		const toetsrenteLaag = Math.min(
			toetsrente1 ?? (toetsrente2 !== undefined ? toetsrente2 + 1 : 0),
			toetsrente2 ?? (toetsrente1 !== undefined ? toetsrente1 + 1 : 0),
		);
		const toetsrenteHoog = Math.max(
			toetsrente1 ?? (toetsrente2 !== undefined ? toetsrente2 - 1 : 0),
			toetsrente2 ?? (toetsrente1 !== undefined ? toetsrente1 - 1 : 0),
		);

		let looptijd1: number | undefined;
		if (typeof state.values.looptijdDeel1 === "number") {
			looptijd1 = state.values.looptijdDeel1;
		}

		let looptijd2: number | undefined;
		if (typeof state.values.looptijdDeel2 === "number") {
			looptijd2 = state.values.looptijdDeel2;
		}

		const laagsteLooptijd = Math.min(
			looptijd1 ?? Number.MAX_SAFE_INTEGER,
			looptijd2 ?? Number.MAX_SAFE_INTEGER,
		);

		const p1 = (toetsrente1 ?? 0) * (looptijd1 ?? 0);
		const p2 = (toetsrente2 ?? 0) * (looptijd2 ?? 0);

		const jaarVanAfsluiten = calculationDate.value.year;

		let toetsinkomen = 0;
		if (typeof state.values.toetsinkomen === "number") {
			toetsinkomen = state.values.toetsinkomen;
		}

		const bkrVermelding = asCur(klantgegevens.state.values.bkrVermelding) ?? 0;
		const erfpachtlastPerMaand =
			(asCur(data.value.objectData.erfpacht?.canonYearly) ?? 0) / 12;

		// Calculate income from inventarisation data instead of klantgegevens
		const incomeCalculation = calculateTotalYearlyIncome(
			data.value.income,
			data.value.customers.map((customer) => customer.id),
			calculationDate.value.toISODate() ?? "",
		);
		const inkomenKlant = incomeCalculation.customer1Income;
		const inkomenPartner = incomeCalculation.customer2Income;

		// Calculate AOW eligibility based on customer birth dates
		const klantBirthDate = data.value.customers[0]?.personDetails?.birthDate;
		const partnerBirthDate = data.value.customers[1]?.personDetails?.birthDate;
		const klantAOW = isEligibleForAOW(
			klantBirthDate,
			calculationDateString.value,
		);
		const partnerAOW = isEligibleForAOW(
			partnerBirthDate,
			calculationDateString.value,
		);

		const aow = inkomenKlant >= inkomenPartner ? klantAOW : partnerAOW;

		const toetsHuishouden: ToetsHuishoudenParameters = {
			toetsinkomen,
			aow,
			box1: true,
			bkrVermelding,
			erfpachtlastPerMaand,
		};

		if (!Number.isFinite(toetsinkomen)) {
			state.values.error =
				"Vul een geldig toetsinkomen in voor de optimalisatie van de leningdeelhoogtes.";
		}

		const woonquoteCutoffs = getWoonquoteCutoffs(
			toetsHuishouden,
			jaarVanAfsluiten,
			toetsrenteLaag,
			toetsrenteHoog,
		);

		if (!Array.isArray(woonquoteCutoffs)) {
			return;
		}

		const toetsrentes = [toetsrenteLaag, ...woonquoteCutoffs, toetsrenteHoog];

		let optimaleHypotheek: OptimaleHypotheek = { hoogte: 0 };
		for (const wqRente of toetsrentes) {
			const hypotheek: OptimaleHypotheek = getOptimaleHypotheekHoogte(
				toetsHuishouden,
				wqRente,
				jaarVanAfsluiten,
				laagsteLooptijd,
			);

			// If one loanpart is incomplete or undefined, the other part is assigned the optimal height
			if (
				deel2 === undefined ||
				(toetsrente2 === undefined && looptijd2 === undefined)
			) {
				hypotheek.hoogte1 = hypotheek.hoogte;
				optimaleHypotheek = hypotheek;
				break;
			}

			if (
				deel1 === undefined ||
				(toetsrente1 === undefined && looptijd1 === undefined)
			) {
				hypotheek.hoogte2 = hypotheek.hoogte;
				optimaleHypotheek = hypotheek;
				break;
			}

			// hoogteDeel1 * ratio = hoogteDeel2
			const dividend = looptijd1 !== undefined ? p1 - wqRente * looptijd1 : NaN;
			const divisor = looptijd2 !== undefined ? wqRente * looptijd2 - p2 : NaN;

			const ratio =
				Number.isFinite(dividend) && Number.isFinite(divisor) && divisor !== 0
					? dividend / divisor
					: NaN;

			if (hypotheek.hoogte !== undefined) {
				hypotheek.hoogte1 = hypotheek.hoogte / (ratio + 1);
				hypotheek.hoogte2 = hypotheek.hoogte - hypotheek.hoogte1;
			} else {
				hypotheek.hoogte1 = NaN;
				hypotheek.hoogte2 = NaN;
			}

			const minDeelHoogte = useParameters(
				calculationDateString.value,
			).minDeelHoogte;

			if (hypotheek.hoogte1 < minDeelHoogte && hypotheek.hoogte1 !== 0) {
				const delta = minDeelHoogte - hypotheek.hoogte1;
				hypotheek.hoogte1 += delta;
				hypotheek.hoogte2 -= delta;
			} else if (hypotheek.hoogte2 < minDeelHoogte && hypotheek.hoogte2 !== 0) {
				const delta = minDeelHoogte - hypotheek.hoogte2;
				hypotheek.hoogte2 += delta;
				hypotheek.hoogte1 -= delta;
			}

			if (
				hypotheek.hoogte !== undefined &&
				hypotheek.hoogte > (optimaleHypotheek.hoogte ?? 0) &&
				((hypotheek.hoogte1 !== undefined &&
					hypotheek.hoogte2 !== undefined &&
					hypotheek.hoogte1 >= minDeelHoogte &&
					hypotheek.hoogte2 >= minDeelHoogte) ||
					hypotheek.hoogte1 == 0 ||
					hypotheek.hoogte2 == 0)
			) {
				optimaleHypotheek = hypotheek;
			}
		}

		const huishoudenWoonquote: ToetsHuishoudenWoonqoute = {
			toetsinkomen: asCur(computedValues.value.toetsinkomen) ?? 0,
			bkrVermelding: toetsHuishouden.bkrVermelding,
			erfpachtlastPerMaand: toetsHuishouden.erfpachtlastPerMaand,
			woonquote: optimaleHypotheek.woonquote ?? NaN,
		};
		const toegestaneMaandlast =
			leningdelenStore.getToegestaneMaandlast(huishoudenWoonquote);

		let maxLening: number | undefined = 0;
		const maxPasses = 50;
		for (let i = 0; i < maxPasses; i++) {
			optimaleHypotheek.hoogte1 = Math.floor(optimaleHypotheek.hoogte1 ?? 0);
			optimaleHypotheek.hoogte2 = Math.floor(optimaleHypotheek.hoogte2 ?? 0);
			optimaleHypotheek.hoogte =
				(optimaleHypotheek.hoogte1 ?? 0) + (optimaleHypotheek.hoogte2 ?? 0);

			const toetslast1 = leningdelenStore.getToetslastPerMaand(
				optimaleHypotheek.hoogte1 ?? 0,
				toetsrente1 ?? 0,
				looptijd1 ?? 0,
				true,
			);

			const toetslast2 = leningdelenStore.getToetslastPerMaand(
				optimaleHypotheek.hoogte2 ?? 0,
				toetsrente2 ?? 0,
				looptijd2 ?? 0,
				true,
			);

			const gewogenRente =
				((optimaleHypotheek.hoogte1 ?? 0) *
					(toetsrente1 ?? 0) *
					(looptijd1 ?? 0) +
					(optimaleHypotheek.hoogte2 ?? 0) *
						(toetsrente2 ?? 0) *
						(looptijd2 ?? 0)) /
				((optimaleHypotheek.hoogte1 ?? 0) * (looptijd1 ?? 0) +
					(optimaleHypotheek.hoogte2 ?? 0) * (looptijd2 ?? 0));

			const toetsAnnuiteitenFactor = leningdelenStore.getAnnuiteitenfactor(
				gewogenRente,
				asCur(mortgagePartsTotal.value?.looptijd) ?? NaN,
			);

			maxLening = leningdelenStore.getMaxLeningTotal(
				toegestaneMaandlast,
				(toetslast1 ?? 0) + (toetslast2 ?? 0),
				toetsAnnuiteitenFactor,
				optimaleHypotheek.hoogte,
			);

			const heightRatio =
				maxLening !== undefined && optimaleHypotheek.hoogte
					? maxLening / optimaleHypotheek.hoogte
					: NaN;

			if (heightRatio < 1) {
				const minDeelHoogte = useParameters(
					calculationDateString.value,
				).minDeelHoogte;

				optimaleHypotheek.hoogte1 = Math.max(
					(optimaleHypotheek.hoogte1 ?? 0) * heightRatio,
					minDeelHoogte,
				);

				optimaleHypotheek.hoogte2 = Math.max(
					(optimaleHypotheek.hoogte2 ?? 0) * heightRatio,
					minDeelHoogte,
				);

				optimaleHypotheek.hoogte =
					(optimaleHypotheek.hoogte1 ?? 0) + (optimaleHypotheek.hoogte2 ?? 0);
			} else {
				if (
					(optimaleHypotheek.hoogte1 ?? 0) > (optimaleHypotheek.hoogte2 ?? 0)
				) {
					optimaleHypotheek.hoogte1 = (optimaleHypotheek.hoogte1 ?? 0) - 1;
				} else {
					optimaleHypotheek.hoogte2 = (optimaleHypotheek.hoogte2 ?? 0) - 1;
				}

				optimaleHypotheek.hoogte1 = Math.max(optimaleHypotheek.hoogte1 ?? 0, 0);
				optimaleHypotheek.hoogte2 = Math.max(optimaleHypotheek.hoogte2 ?? 0, 0);

				optimaleHypotheek.hoogte =
					(optimaleHypotheek.hoogte1 ?? 0) + (optimaleHypotheek.hoogte2 ?? 0);

				break;
			}
		}

		if (
			(optimaleHypotheek.hoogte1 ?? 0) + (optimaleHypotheek.hoogte2 ?? 0) >
			(maxLening ?? 0)
		) {
			state.values.error =
				"De leninghoogtes konden niet geoptimaliseerd worden.";

			return;
		}

		if (!Number.isFinite(optimaleHypotheek.hoogte1)) {
			optimaleHypotheek.hoogte1 = undefined;
		}
		state.values.optimaleHoogteDeel1 = optimaleHypotheek.hoogte1;

		if (!Number.isFinite(optimaleHypotheek.hoogte2)) {
			optimaleHypotheek.hoogte2 = undefined;
		}
		state.values.optimaleHoogteDeel2 = optimaleHypotheek.hoogte2;

		if (!Number.isFinite(optimaleHypotheek.hoogte)) {
			optimaleHypotheek.hoogte = undefined;
		}
		state.values.maximaleHypotheek = optimaleHypotheek.hoogte;
	});

	function pasHoogtesToe() {
		if (
			data.value.mortgage.mortgageParts.length > 0 &&
			!confirm(
				"Door de hoogtes toe te passen schrijf je de hoogte van de bestaande leningdelen over. Wil je de hoogtes overschrijven?",
			)
		) {
			return;
		}

		if (data.value.mortgage.mortgageParts[0] !== undefined) {
			let part1Hoogte: number | undefined;

			if (
				typeof state.values.optimaleHoogteDeel1 === "number" ||
				typeof state.values.optimaleHoogteDeel1 === "string"
			) {
				part1Hoogte = state.values.optimaleHoogteDeel1;
			}

			data.value.mortgage.mortgageParts[0].amount = part1Hoogte;
		}

		if (data.value.mortgage.mortgageParts[1] !== undefined) {
			let part2Hoogte: number | undefined;
			if (
				typeof state.values.optimaleHoogteDeel2 === "number" ||
				typeof state.values.optimaleHoogteDeel2 === "string"
			) {
				part2Hoogte = state.values.optimaleHoogteDeel2;
			}

			data.value.mortgage.mortgageParts[1].amount = part2Hoogte;
		}
	}

	return { state, pasHoogtesToe };
}
